import * as React from "react";
import { Alert, Chip, Divider, LinearProgress, Stack } from "@mui/material";
import { useQuery } from "@apollo/client";
import {
  GET_BILLING_SUBSCRIPTION,
  GET_BUSINESS_PAYOUT,
} from "../../../../../platform/graphql/queries/generic";

export function SubscriptionSummary({ businessId }: { businessId: number }) {
  const { data, loading, error } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId: String(businessId) },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
  });

  if (!businessId) return null;
  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="warning">Unable to load billing subscription.</Alert>;

  const subscription = data?.getBillingSubscription;
  if (!subscription?.id) {
    return <Alert severity="info">No billing subscription is attached to this business.</Alert>;
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={`Status: ${subscription.status || "Unknown"}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`Seats ${Number(subscription?.residentCount || 0)}/${Number(
            subscription?.residentSeats || 0,
          )}`}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`Plan ${subscription?.planId ?? "-"}`}
        />
      </Stack>
    </Stack>
  );
}

export function PayoutSummary({ businessId }: { businessId: number }) {
  const { data, loading, error } = useQuery(GET_BUSINESS_PAYOUT, {
    variables: { businessId: String(businessId) },
    skip: !businessId,
    fetchPolicy: "cache-and-network",
  });

  if (!businessId) return null;
  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="warning">Unable to load payout settings.</Alert>;

  const payout = data?.getBusinessPayout;
  if (!payout?.provider && !payout?.identifier) {
    return <Alert severity="info">No payout method is configured yet.</Alert>;
  }

  return (
    <React.Fragment>
      <Divider />
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip size="small" variant="outlined" label={`Provider: ${payout?.provider || "-"}`} />
        <Chip size="small" variant="outlined" label={payout?.identifier || "No identifier"} />
      </Stack>
    </React.Fragment>
  );
}
